/**
 * Edit / add the resume details on the review page's Resume match card.
 *
 * What the scan read is never overwritten: a recruiter's values are saved apart (PUT
 * /candidacies/{id}/resume-details → candidacies.resume_overrides, 0106), so a Rescan keeps them. Only
 * the fields that actually changed are sent; "Reset" sends null for a key, which falls back to the
 * scanned value. Not scored — these are context for a person.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import type { ResumeOverrideKey, ResumeOverridesInput } from "@talenthop/shared";
import { api, ApiError } from "../../lib/api.js";
import type { ResumeProfileData } from "./ResumeProfileView.js";

type Edu = { degree: string; institution: string; year: string };

const csv = (a: readonly string[] | undefined): string => (a ?? []).join(", ");
const parseCsv = (s: string): string[] => Array.from(new Set(s.split(/[,\n]/).map((x) => x.trim()).filter(Boolean)));
const sameList = (a: readonly string[] | undefined, b: readonly string[]): boolean =>
  (a ?? []).length === b.length && (a ?? []).every((x, i) => x === b[i]);

export function ResumeProfileEditor({
  candidacyId,
  profile,
  overridden,
  onCancel,
  onSaved,
}: {
  candidacyId: string;
  profile: ResumeProfileData;
  overridden: readonly ResumeOverrideKey[];
  onCancel: () => void;
  onSaved: () => void;
}): ReactNode {
  const places = profile.citiesWorked?.length ? profile.citiesWorked : profile.city ? [profile.city] : [];
  const [years, setYears] = useState(profile.totalYears != null ? String(profile.totalYears) : "");
  const [company, setCompany] = useState(profile.lastCompany ?? "");
  const [title, setTitle] = useState(profile.lastTitle ?? "");
  const [current, setCurrent] = useState(profile.currentlyWorking === true);
  const [month, setMonth] = useState(profile.lastWorkingYm ?? "");
  const [edu, setEdu] = useState<Edu[]>(
    (profile.education ?? []).map((e) => ({ degree: e.degree, institution: e.institution ?? "", year: e.year ?? "" })),
  );
  const [phone, setPhone] = useState(profile.phone ?? "");
  const [email, setEmail] = useState(profile.email ?? "");
  const [cities, setCities] = useState(csv(places));
  const [skills, setSkills] = useState(csv(profile.skills));
  const [reset, setReset] = useState<Set<ResumeOverrideKey>>(new Set());

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      const set = (k: ResumeOverrideKey, changed: boolean, value: unknown): void => {
        if (reset.has(k)) body[k] = null;
        else if (changed) body[k] = value;
      };
      const y = years.trim() === "" ? undefined : Number(years);
      set("totalYears", y !== undefined && y !== profile.totalYears, y);
      set("lastCompany", company.trim() !== "" && company.trim() !== (profile.lastCompany ?? ""), company.trim());
      set("lastTitle", title.trim() !== "" && title.trim() !== (profile.lastTitle ?? ""), title.trim());
      set("currentlyWorking", current && profile.currentlyWorking !== true, true);
      set("lastWorkingYm", !current && month !== "" && month !== (profile.lastWorkingYm ?? ""), month);
      const cleanEdu = edu
        .map((e) => ({
          degree: e.degree.trim(),
          ...(e.institution.trim() ? { institution: e.institution.trim() } : {}),
          ...(e.year.trim() ? { year: e.year.trim() } : {}),
        }))
        .filter((e) => e.degree);
      set("education", cleanEdu.length > 0 && JSON.stringify(cleanEdu) !== JSON.stringify(profile.education ?? []), cleanEdu);
      set("phone", phone.trim() !== "" && phone.trim() !== (profile.phone ?? ""), phone.trim());
      set("email", email.trim() !== "" && email.trim() !== (profile.email ?? ""), email.trim());
      const c = parseCsv(cities);
      set("citiesWorked", c.length > 0 && !sameList(places, c), c);
      const s = parseCsv(skills);
      set("skills", s.length > 0 && !sameList(profile.skills, s), s);
      // The working month and "currently working" are one fact: setting one clears the other's override.
      if (body.currentlyWorking === true && body.lastWorkingYm === undefined && overridden.includes("lastWorkingYm")) {
        body.lastWorkingYm = null;
      }
      if (body.lastWorkingYm && body.currentlyWorking === undefined && overridden.includes("currentlyWorking")) {
        body.currentlyWorking = null;
      }
      return api.put(`/candidacies/${candidacyId}/resume-details`, body as ResumeOverridesInput);
    },
    onSuccess: onSaved,
  });

  const resetBtn = (k: ResumeOverrideKey): ReactNode =>
    overridden.includes(k) ? (
      <button
        type="button"
        className="btn ghost sm"
        aria-pressed={reset.has(k)}
        onClick={() =>
          setReset((r) => {
            const n = new Set(r);
            if (n.has(k)) n.delete(k);
            else n.add(k);
            return n;
          })
        }
      >
        {reset.has(k) ? "Will reset to scanned value" : "Reset to scanned"}
      </button>
    ) : null;

  const row = (label: string, node: ReactNode, key?: ResumeOverrideKey): ReactNode => (
    <label className="t1-rv-edit-row">
      <span className="t1-rv-edit-l">{label}</span>
      <span className="t1-rv-edit-f">{node}</span>
      {key && resetBtn(key)}
    </label>
  );

  return (
    <form
      className="t1-rv-edit"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {row(
        "Experience (years)",
        <input type="number" min={0} max={60} step={0.5} value={years} onChange={(e) => setYears(e.target.value)} />,
        "totalYears",
      )}
      {row("Last company", <input value={company} maxLength={120} onChange={(e) => setCompany(e.target.value)} />, "lastCompany")}
      {row("Last title", <input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />, "lastTitle")}
      {row(
        "Last working date",
        <>
          <input type="month" value={month} disabled={current} onChange={(e) => setMonth(e.target.value)} />{" "}
          <span style={{ whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={current} onChange={(e) => setCurrent(e.target.checked)} /> Currently working
          </span>
        </>,
        overridden.includes("lastWorkingYm") ? "lastWorkingYm" : "currentlyWorking",
      )}
      <div className="t1-rv-edit-row">
        <span className="t1-rv-edit-l">Education</span>
        <span className="t1-rv-edit-f">
          {edu.map((e, i) => (
            <span key={i} className="t1-rv-edit-edu">
              <input
                placeholder="Degree"
                value={e.degree}
                onChange={(ev) => setEdu(edu.map((x, j) => (j === i ? { ...x, degree: ev.target.value } : x)))}
              />
              <input
                placeholder="Institution"
                value={e.institution}
                onChange={(ev) => setEdu(edu.map((x, j) => (j === i ? { ...x, institution: ev.target.value } : x)))}
              />
              <input
                placeholder="Year"
                value={e.year}
                style={{ width: 70 }}
                onChange={(ev) => setEdu(edu.map((x, j) => (j === i ? { ...x, year: ev.target.value } : x)))}
              />
              <button type="button" className="btn ghost sm" aria-label="Remove education" onClick={() => setEdu(edu.filter((_, j) => j !== i))}>
                ×
              </button>
            </span>
          ))}
          {edu.length < 8 && (
            <button type="button" className="btn ghost sm" onClick={() => setEdu([...edu, { degree: "", institution: "", year: "" }])}>
              + Add education
            </button>
          )}
        </span>
        {resetBtn("education")}
      </div>
      {row("Phone", <input value={phone} maxLength={30} onChange={(e) => setPhone(e.target.value)} />, "phone")}
      {row("Email", <input type="email" value={email} maxLength={200} onChange={(e) => setEmail(e.target.value)} />, "email")}
      {row("Cities", <input value={cities} placeholder="Comma separated" onChange={(e) => setCities(e.target.value)} />, "citiesWorked")}
      {row("Skills", <input value={skills} placeholder="Comma separated" onChange={(e) => setSkills(e.target.value)} />, "skills")}
      {save.isError && (
        <p className="mut sm" role="alert">
          {save.error instanceof ApiError ? save.error.message : "Could not save. Please try again."}
        </p>
      )}
      <div className="t1-rv-edit-actions">
        <button type="submit" className="btn primary sm" disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save"}
        </button>
        <button type="button" className="btn ghost sm" onClick={onCancel} disabled={save.isPending}>
          Cancel
        </button>
      </div>
    </form>
  );
}
